import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../../components/common/header";
import BottomSheet from "../../../../components/common/bottom-sheet";
import { gql, useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { UPDATE_RESERVE_TIME } from "../../api/reserve-queries";
import { GET_PACK_UNPROCESSED_ORDER_COUNT } from "../../page";
import { track } from "@amplitude/analytics-browser";
import { notifyError } from "../../../../utils/toast";

const GET_CONFIRMED_PACK_ORDER_COUNT = gql`
  query getConfirmedPackOrderCount {
    getConfirmedPackOrderCount
  }
`;
// 내부적으로 요일은 0 ~ 6으로 관리 (0: 월요일, 1: 화요일, …, 6: 일요일)
const daysOfWeek: number[] = [0, 1, 2, 3, 4, 5, 6];
// UI 표기를 위한 한글 요일 배열
const dayLabels: string[] = ["월", "화", "수", "목", "금", "토", "일"];

interface TimeRange {
  start: string;
  end: string;
  enabled: boolean;
}

type TimeData = Record<number, TimeRange>;

const isValidTimeRange = (start: string, end: string): boolean => {
  if (!start || !end) return false; // 빈 값 체크 추가

  // 23:30은 예외적으로 허용
  if (start === "23:30" && end === "23:55") return true;

  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  // 유효하지 않은 시간 형식 체크
  if (
    isNaN(startHour) ||
    isNaN(startMinute) ||
    isNaN(endHour) ||
    isNaN(endMinute)
  ) {
    return false;
  }

  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;

  return endTotalMinutes - startTotalMinutes >= 30;
};

const SignupPickupTime: React.FC = () => {
  const navigate = useNavigate();
  const [representativeTime, setRepresentativeTime] = useState<TimeRange>({
    start: "",
    end: "",
    enabled: false,
  });
  const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set());
  const [selectedType, setSelectedType] = useState<"start" | "end" | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string>("00");
  const [selectedMinute, setSelectedMinute] = useState<string>("00");
  const [isSaving, setIsSaving] = useState(false);
  const { data, loading, error } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
  });
  const [updateReserveTime] = useMutation(UPDATE_RESERVE_TIME);
  const { data: packOrderCountData } = useQuery(
    GET_PACK_UNPROCESSED_ORDER_COUNT,
    {
      fetchPolicy: "network-only",
    },
  );
  const { data: confirmedPackOrderCountData } = useQuery(
    GET_CONFIRMED_PACK_ORDER_COUNT,
    {
      fetchPolicy: "network-only",
    },
  );
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);

  // 서버에서 받은 reserveTime 데이터를 state에 초기화
  useEffect(() => {
    if (data && data.getSellerStore && data.getSellerStore.reserveTime) {
      const reserveTimeFromServer = data.getSellerStore.reserveTime;

      // 활성화된 요일들 찾기
      const activeDays = new Set<number>();
      let firstActiveTime: TimeRange | null = null;

      daysOfWeek.forEach((day) => {
        if (reserveTimeFromServer[day]?.enabled) {
          activeDays.add(day);
          if (!firstActiveTime) {
            firstActiveTime = reserveTimeFromServer[day];
          }
        }
      });

      setSelectedDays(activeDays);
      if (firstActiveTime) {
        setRepresentativeTime(firstActiveTime);
      }
    }
  }, [data]);

  // 바텀시트가 열릴 때 현재 선택된 시간으로 스크롤 위치 조정
  useEffect(() => {
    if (isModalOpen && hourScrollRef.current && minuteScrollRef.current) {
      const hourIndex = parseInt(selectedHour);
      const minuteIndex = Math.floor(parseInt(selectedMinute) / 5);

      const itemHeight = 40; // py-2 텍스트의 높이 (8px * 2) + text-lg (32px) = 48px

      hourScrollRef.current.scrollTop = hourIndex * itemHeight;
      minuteScrollRef.current.scrollTop = minuteIndex * itemHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  // 매장이 없을 경우 처리
  if (loading) return <p>로딩 중...</p>;
  if (error) return <p>오류: {error.message}</p>;

  if (!data?.getSellerStore) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );
  }

  const handleToggleDay = (day: number) => {
    if (confirmedPackOrderCountData?.getConfirmedPackOrderCount > 0) {
      alert("확정된 예약이 있어 시간을 변경할 수 없습니다.");
      return;
    }
    if (packOrderCountData?.getUnProcessedPackOrderCount > 0) {
      alert(
        "주문 처리가 안 된 예약이 있습니다. 주문처리를 완료하고 시간을 설정해주세요.",
      );
      return;
    }

    setSelectedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      return newSet;
    });
  };

  const handleOpenModal = (type: "start" | "end") => {
    if (confirmedPackOrderCountData?.getConfirmedPackOrderCount > 0) {
      alert("확정된 예약이 있어 시간을 변경할 수 없습니다.");
      return;
    }
    if (packOrderCountData?.getPackUnprocessedOrderCount > 0) {
      alert(
        "주문 처리가 안 된 예약이 있습니다. 주문처리를 완료하고 시간을 설정해주세요.",
      );
      return;
    }

    setSelectedType(type);
    const currentTime = representativeTime[type] || "18:00";
    const [hour, minute] = currentTime.split(":");
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setIsModalOpen(true);
  };

  // 시작 시간을 설정하면 종료 시간을 자동으로 30분 뒤로 계산 (최대 23:55까지)
  const getNextTime = (hour: string, minute: string) => {
    let nextMinute = parseInt(minute) + 60;
    let nextHour = parseInt(hour);
    if (nextMinute >= 60) {
      nextHour += 1;
      nextMinute %= 60;
    }
    if (nextHour >= 24) {
      nextHour = 23;
      nextMinute = 55;
    }
    return `${String(nextHour).padStart(2, "0")}:${String(nextMinute).padStart(
      2,
      "0",
    )}`;
  };

  const handleSaveTime = () => {
    if (!selectedType) return;

    const newTime = `${selectedHour}:${selectedMinute}`;

    setRepresentativeTime((prev) => {
      const updated = { ...prev };

      if (selectedType === "start") {
        updated.start = newTime;

        // 종료 시간이 없거나 유효하지 않은 경우 자동 설정
        if (!updated.end || !isValidTimeRange(newTime, updated.end)) {
          const nextTime = getNextTime(selectedHour, selectedMinute);
          updated.end = nextTime;
        }
      } else {
        // 종료 시간 설정 시 유효성 검사
        if (!isValidTimeRange(updated.start, newTime)) {
          return prev;
        }
        updated.end = newTime;
      }

      updated.enabled = true;
      return updated;
    });

    // 종료 시간 설정 시 유효성 검사를 여기서 한 번만 수행
    if (selectedType === "end") {
      if (!isValidTimeRange(representativeTime.start, newTime)) {
        alert("종료 시간은 시작 시간보다 최소 30분 늦어야 합니다!");
        return;
      }
    }

    setIsModalOpen(false);
  };

  const handleSubmit = async () => {
    // 활성화된 요일이 있는지 확인
    if (selectedDays.size === 0) {
      alert("최소 하나의 요일을 선택해주세요.");
      return;
    }

    // 대표 시간이 올바르게 설정되었는지 확인
    if (
      !representativeTime.start ||
      !representativeTime.end ||
      !isValidTimeRange(representativeTime.start, representativeTime.end)
    ) {
      alert("시간을 올바르게 설정해주세요.");
      return;
    }

    if (!window.confirm("픽업시간을 저장할까요?")) {
      return;
    }

    const finalTimeData: TimeData = {} as TimeData;
    daysOfWeek.forEach((day) => {
      finalTimeData[day] = {
        start: representativeTime.start,
        end: representativeTime.end,
        enabled: selectedDays.has(day),
      };
    });

    setIsSaving(true);
    try {
      await updateReserveTime({
        variables: {
          id: data?.getSellerStore.id,
          reserveTime: finalTimeData,
          reason: "",
        },
      });

      track("사장님 입점신청9 예약판매 픽업시간 설정 완료");
      navigate("/boss/signup/complete");
    } catch (error) {
      notifyError(
        error instanceof Error
          ? error.message
          : "픽업시간 저장에 실패했습니다. 다시 시도해주세요.",
      );
    } finally {
      setIsSaving(false);
    }
  };
  const finalTimeData: TimeData = {} as TimeData;
  console.log(
    daysOfWeek.forEach((day) => {
      finalTimeData[day] = {
        start: representativeTime.start,
        end: representativeTime.end,
        enabled: selectedDays.has(day),
      };
    }),
  );
  console.log(daysOfWeek);
  console.log(selectedDays);
  console.log(representativeTime);

  return (
    <div className="flex flex-col min-h-screen h-full bg-white">
      <Header title="입점 신청" />

      <div className="flex justify-center p-4">
        <div className="w-full space-y-6">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">예약 판매 픽업시간 설정</h1>
            <p className="text-gray-700 break-keep">
              픽업시간을 설정하시면 손님이 픽업시간 팩을 픽업하러 오게 돼요.
            </p>
            <div className="bg-gray-100 rounded-md p-4">
              <p className="text-gray-900 font-bold break-keep">
                이제 마지막 단계에요!
              </p>
            </div>
            <div className="bg-gray-100 rounded-md p-4">
              <p className="text-gray-700 break-keep">
                원하는 시간과 요일을 선택해주세요!
              </p>
              <br />
              <p className="text-gray-700 break-keep">
                등록하신 정보는 입점 완료 후 수정 가능하니 편하게 입력해주세요.
              </p>
              <br />
              <p className="text-gray-700 break-keep">
                요일별 시간 변경도 추후 가능합니다!
              </p>
            </div>
          </div>

          {/* 1단계: 대표 시간 설정 */}
          <div className="px-4 space-y-4">
            <h2 className="text-lg font-semibold">1. 픽업시간 설정</h2>
            <div className="flex items-center justify-center space-x-4 p-4 border rounded-lg">
              <button
                className="border rounded p-3 w-28 bg-green-200 text-center font-medium"
                onClick={() => handleOpenModal("start")}
              >
                {representativeTime.start || "시작시간"}
              </button>
              <span className="text-lg font-bold">~</span>
              <button
                className={clsx(
                  "border rounded p-3 w-28 bg-green-200 text-center font-medium",
                  { "opacity-30": !representativeTime.start },
                )}
                onClick={() =>
                  representativeTime.start && handleOpenModal("end")
                }
              >
                {representativeTime.end || "종료시간"}
              </button>
            </div>
          </div>

          {/* 2단계: 요일 선택 */}
          <div className="px-4 space-y-4">
            <h2 className="text-lg font-semibold">2. 픽업 가능 요일 선택</h2>
            <div className="space-y-2">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className="flex items-center space-x-3 py-3 border-b last:border-none"
                >
                  <input
                    type="checkbox"
                    checked={selectedDays.has(day)}
                    onChange={() => handleToggleDay(day)}
                    className="w-5 h-5 accent-blue-500"
                  />
                  <span className="text-lg font-medium">
                    {dayLabels[day]}요일
                  </span>
                  {selectedDays.has(day) &&
                    representativeTime.start &&
                    representativeTime.end && (
                      <span className="text-sm text-gray-600">
                        {representativeTime.start} ~ {representativeTime.end}
                      </span>
                    )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <button
              onClick={() => void handleSubmit()}
              disabled={isSaving}
              className="w-full py-3 bg-primary text-white font-semibold rounded-xl transition duration-150 disabled:opacity-50"
            >
              {isSaving ? "저장 중..." : "다음"}
            </button>
          </div>
        </div>
      </div>

      <BottomSheet
        isOpen={isModalOpen}
        mainText="시간 선택"
        onClose={() => setIsModalOpen(false)}
        onButtonClick={handleSaveTime}
        buttonText="저장하기"
        disableDrag={true}
      >
        <div className="flex justify-center py-4 space-x-6">
          {/* 시간 선택 */}
          <div
            ref={hourScrollRef}
            className="overflow-y-auto max-h-60 w-24 text-center"
          >
            {Array.from({ length: 24 }, (_, hour) => {
              const hourStr = String(hour).padStart(2, "0");
              return (
                <button
                  key={hourStr}
                  className={`w-full py-2 text-lg ${
                    selectedHour === hourStr
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100"
                  }`}
                  onClick={(e) => {
                    setSelectedHour(hourStr);
                  }}
                >
                  {hourStr}
                </button>
              );
            })}
          </div>
          <span className="text-lg font-bold pt-2">:</span>
          {/* 분 선택 */}
          <div
            ref={minuteScrollRef}
            className="overflow-y-auto max-h-60 w-24 text-center"
          >
            {Array.from({ length: 12 }, (_, i) => {
              const minute = String(i * 5).padStart(2, "0");
              return (
                <button
                  key={minute}
                  className={`w-full py-2 text-lg ${
                    selectedMinute === minute
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100"
                  }`}
                  onClick={(e) => {
                    setSelectedMinute(minute);
                  }}
                >
                  {minute}
                </button>
              );
            })}
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default SignupPickupTime;
